import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Form, Input, Select, Tag, message } from 'antd';
import { getAiChatApiBase } from '../../aiChat/config';
import {
  listAgents,
  getAgentById,
  getAiChatAgentRequestErrorMessage,
} from '../../aiChat/api/agentsAdminApi';
import { extractLakehouseFields } from '../../aiChat/api/normalizeChatbotAgentRow';

const VISIBILITY_OPTIONS = [
  { value: 'admin', label: 'Admins' },
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
  { value: 'allmembers', label: 'All project members' },
];

/** Admin cannot be toggled off from the dropdown (value is still enforced in `getValueFromEvent`). */
const VISIBILITY_SELECT_OPTIONS = VISIBILITY_OPTIONS.map((o) =>
  o.value === 'admin' ? { ...o, disabled: true } : o,
);

const ENV_ALERT =
  'Set REACT_APP_AI_CHAT_API_BASE (for example http://localhost:3001/api/v1) to load agents from the chatbot API.';

function normalizeVisibilityToken(t) {
  const x = String(t).trim().toLowerCase();
  if (x === 'admins') {
    return 'admin';
  }
  return x;
}

export function parseVisibilityFromApi(v) {
  if (Array.isArray(v)) {
    return v.map((x) => normalizeVisibilityToken(x)).filter(Boolean);
  }
  if (typeof v === 'string' && v.trim()) {
    return v
      .split(',')
      .map((p) => normalizeVisibilityToken(p))
      .filter(Boolean);
  }
  return [];
}

/** `admin` is always first; deduped extras follow (admin cannot be removed). */
export function mergeVisibilityWithAdmin(tokens) {
  const normalized = (tokens || [])
    .map((t) => normalizeVisibilityToken(t))
    .filter(Boolean);
  const extras = normalized.filter((t) => t !== 'admin');
  const out = ['admin'];
  for (const x of extras) {
    if (!out.includes(x)) {
      out.push(x);
    }
  }
  return out;
}

function visibilitySelectTagRender(props) {
  const { label, value, closable, onClose } = props;
  const isAdmin = normalizeVisibilityToken(value) === 'admin';
  const onPreventMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag
      onMouseDown={onPreventMouseDown}
      closable={!isAdmin && closable}
      onClose={isAdmin ? undefined : onClose}
      style={{ marginRight: 3 }}
    >
      {label}
    </Tag>
  );
}

export function emptyDefaultChatbotFormState() {
  return {
    agentId: '',
    lakehouseName: '',
    lakehouseSQLEndpoint: '',
    customInstruction: '',
    introMessage: '',
    visibility: ['admin'],
  };
}

/**
 * @param {object | null | undefined} raw — API `defaultChatbotConfig`
 * @returns {ReturnType<typeof emptyDefaultChatbotFormState>}
 */
export function normalizeDefaultChatbotFromApi(raw) {
  if (!raw || typeof raw !== 'object') {
    return emptyDefaultChatbotFormState();
  }
  let visibility = parseVisibilityFromApi(raw.visibility);
  if (!visibility.length) {
    visibility = ['admin'];
  } else {
    visibility = mergeVisibilityWithAdmin(visibility);
  }
  const rootLh = extractLakehouseFields(raw);
  const agentLh =
    raw.agent && typeof raw.agent === 'object'
      ? extractLakehouseFields(raw.agent)
      : { lakehouseName: '', lakehouseSQLEndpoint: '' };
  return {
    agentId: raw.agentId || raw.agent?.id || '',
    lakehouseName: rootLh.lakehouseName || agentLh.lakehouseName || '',
    lakehouseSQLEndpoint:
      rootLh.lakehouseSQLEndpoint || agentLh.lakehouseSQLEndpoint || '',
    customInstruction: raw.customInstruction ?? '',
    introMessage: raw.introMessage ?? '',
    visibility,
  };
}

/**
 * POST body fragment for `defaultChatbotConfig`.
 * Backend expects `visibility` as a string (comma-separated tokens), not an array.
 *
 * @param {ReturnType<typeof normalizeDefaultChatbotFromApi>} state
 */
export function buildDefaultChatbotConfigPayload(state) {
  const visibilityTokens = mergeVisibilityWithAdmin(
    Array.isArray(state?.visibility)
      ? state.visibility
      : parseVisibilityFromApi(state?.visibility),
  );
  const visibility = visibilityTokens.join(',');
  return {
    agentId: String(state?.agentId ?? '').trim(),
    lakehouseName: state?.lakehouseName ?? '',
    lakehouseSQLEndpoint: state?.lakehouseSQLEndpoint ?? '',
    customInstruction: state?.customInstruction ?? '',
    introMessage: state?.introMessage ?? '',
    visibility,
  };
}

/**
 * @param {ReturnType<typeof normalizeDefaultChatbotFromApi>} slice
 * @param {{ agentsAvailable: boolean }} opts
 */
export function validateDefaultChatbotSlice(slice, { agentsAvailable }) {
  if (
    agentsAvailable &&
    (!slice?.agentId || String(slice.agentId).trim() === '')
  ) {
    throw new Error('Select an agent');
  }
}

export function mapFormValuesToDefaultChatbotState(values) {
  const vis = Array.isArray(values?.visibility)
    ? values.visibility
    : parseVisibilityFromApi(values?.visibility);
  return {
    agentId: values?.agentId ?? '',
    lakehouseName: values?.lakehouseName ?? '',
    lakehouseSQLEndpoint: values?.lakehouseSQLEndpoint ?? '',
    customInstruction: values?.customInstruction ?? '',
    introMessage: values?.introMessage ?? '',
    visibility: mergeVisibilityWithAdmin(vis),
  };
}

const InstanceDefaultChatbotSettings = forwardRef(
  function InstanceDefaultChatbotSettings(
    { model, formResetKey, onModelChange, formChange, onAgentsAvailableChange },
    ref,
  ) {
    const [form] = Form.useForm();
    const [agents, setAgents] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const apiBase = getAiChatApiBase();
    const agentsCbRef = useRef(onAgentsAvailableChange);
    agentsCbRef.current = onAgentsAvailableChange;
    const modelRef = useRef(model);
    modelRef.current = model;
    const agentsRef = useRef([]);
    useEffect(() => {
      agentsRef.current = agents;
    }, [agents]);

    const syncLakehouseFromAgentId = useCallback(
      async (agentId) => {
        const prev = form.getFieldsValue([
          'lakehouseName',
          'lakehouseSQLEndpoint',
        ]);
        if (!agentId) {
          form.setFieldsValue({
            lakehouseName: '',
            lakehouseSQLEndpoint: '',
          });
          return;
        }
        let name = '';
        let endpoint = '';
        const fromList = agentsRef.current.find((a) => a.id === agentId);
        if (fromList) {
          const ex = extractLakehouseFields(fromList);
          name = ex.lakehouseName;
          endpoint = ex.lakehouseSQLEndpoint;
        }
        if (apiBase && (!name || !endpoint)) {
          try {
            const detail = await getAgentById(agentId);
            if (detail) {
              const ex = extractLakehouseFields(detail);
              name = name || ex.lakehouseName || '';
              endpoint = endpoint || ex.lakehouseSQLEndpoint || '';
            }
          } catch (_) {
            /* keep list / template values */
          }
        }
        form.setFieldsValue({
          lakehouseName: name || prev.lakehouseName || '',
          lakehouseSQLEndpoint: endpoint || prev.lakehouseSQLEndpoint || '',
        });
      },
      [apiBase, form],
    );

    const loadAgents = useCallback(async () => {
      if (!apiBase) {
        setAgents([]);
        agentsCbRef.current?.(false);
        return;
      }
      setAgentsLoading(true);
      try {
        const rows = await listAgents();
        setAgents(rows);
        agentsCbRef.current?.(rows.length > 0);
      } catch (e) {
        message.error(
          getAiChatAgentRequestErrorMessage(e, 'Could not load agents.'),
        );
        setAgents([]);
        agentsCbRef.current?.(false);
      } finally {
        setAgentsLoading(false);
      }
    }, [apiBase]);

    useEffect(() => {
      loadAgents();
    }, [loadAgents]);

    useEffect(() => {
      const m = modelRef.current;
      if (!m) {
        return;
      }
      form.setFieldsValue({
        agentId: m.agentId || undefined,
        lakehouseName: m.lakehouseName,
        lakehouseSQLEndpoint: m.lakehouseSQLEndpoint,
        customInstruction: m.customInstruction,
        introMessage: m.introMessage,
        visibility: mergeVisibilityWithAdmin(m.visibility || []),
      });
      let cancelled = false;
      (async () => {
        await syncLakehouseFromAgentId(m.agentId || undefined);
        if (cancelled) {
          return;
        }
        onModelChange?.(
          mapFormValuesToDefaultChatbotState(form.getFieldsValue()),
        );
      })();
      return () => {
        cancelled = true;
      };
    }, [form, formResetKey, syncLakehouseFromAgentId, onModelChange]);

    useEffect(() => {
      if (agentsLoading || !agents.length) {
        return;
      }
      const agentId = form.getFieldValue('agentId');
      if (!agentId) {
        return;
      }
      let cancelled = false;
      (async () => {
        await syncLakehouseFromAgentId(agentId);
        if (cancelled) {
          return;
        }
        onModelChange?.(
          mapFormValuesToDefaultChatbotState(form.getFieldsValue()),
        );
      })();
      return () => {
        cancelled = true;
      };
    }, [agents, agentsLoading, form, syncLakehouseFromAgentId, onModelChange]);

    useImperativeHandle(
      ref,
      () => ({
        validateFields: () => form.validateFields(),
        getFieldsValue: () => form.getFieldsValue(),
      }),
      [form],
    );

    const agentOptions = useMemo(
      () =>
        agents.map((a) => ({
          value: a.id,
          label: a.displayName || a.name || a.id,
        })),
      [agents],
    );

    const agentRules = useMemo(
      () =>
        agents.length > 0
          ? [{ required: true, message: 'Select an agent' }]
          : [],
      [agents.length],
    );

    const visibilityRules = useMemo(
      () => [
        {
          required: true,
          type: 'array',
          min: 1,
          message: 'Select at least one visibility option',
        },
      ],
      [],
    );

    const handleValuesChange = (changed) => {
      if (Object.prototype.hasOwnProperty.call(changed, 'agentId')) {
        void (async () => {
          await syncLakehouseFromAgentId(changed.agentId);
          formChange?.();
          onModelChange?.(
            mapFormValuesToDefaultChatbotState(form.getFieldsValue()),
          );
        })();
        return;
      }
      formChange?.();
      onModelChange?.(
        mapFormValuesToDefaultChatbotState(form.getFieldsValue()),
      );
    };

    return (
      <div className="manage-module-wrapper instance-default-chatbot-settings">
        {!apiBase ? (
          <Alert
            type="info"
            showIcon
            message={ENV_ALERT}
            style={{ marginBottom: 16 }}
          />
        ) : null}

        {apiBase && !agentsLoading && agents.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message="No agents returned from the server."
            style={{ marginBottom: 16 }}
          />
        ) : null}

        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleValuesChange}
          requiredMark={false}
        >
          <Form.Item label="Agent" name="agentId" rules={agentRules}>
            <Select
              loading={agentsLoading}
              disabled={agentsLoading || agents.length === 0}
              placeholder={
                agentsLoading
                  ? 'Loading agents…'
                  : agents.length === 0
                  ? 'No agents available'
                  : 'Select an agent'
              }
              options={agentOptions}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item
            label="Lakehouse name"
            name="lakehouseName"
            extra="Prefilled when you select an agent; you can change it here."
          >
            <Input autoComplete="off" allowClear />
          </Form.Item>

          <Form.Item
            label="Lakehouse SQL endpoint"
            name="lakehouseSQLEndpoint"
            extra="Prefilled when you select an agent; you can change it here."
          >
            <Input autoComplete="off" allowClear />
          </Form.Item>

          <Form.Item label="Custom instruction" name="customInstruction">
            <Input.TextArea rows={4} allowClear />
          </Form.Item>

          <Form.Item label="Intro message" name="introMessage">
            <Input.TextArea rows={4} allowClear />
          </Form.Item>

          <Form.Item
            label="Visibility"
            name="visibility"
            extra="Admins are always on; the Admins tag cannot be removed."
            rules={visibilityRules}
            getValueFromEvent={(v) =>
              mergeVisibilityWithAdmin(Array.isArray(v) ? v : [])
            }
          >
            <Select
              mode="multiple"
              allowClear={false}
              placeholder="Select visibility"
              options={VISIBILITY_SELECT_OPTIONS}
              tagRender={visibilitySelectTagRender}
            />
          </Form.Item>
        </Form>
      </div>
    );
  },
);

InstanceDefaultChatbotSettings.displayName = 'InstanceDefaultChatbotSettings';

export default InstanceDefaultChatbotSettings;
